import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import BrowsePage from "@/pages/BrowsePage";
import LandingPage from "@/pages/LandingPage";
import AuthPage, { AuthCallback } from "@/pages/AuthPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ContributorProfilePage from "@/pages/ContributorProfilePage";
import Dashboard from "@/pages/Dashboard";
import FunctionPage from "@/pages/FunctionPage";
import ApplicationPage from "@/pages/ApplicationPage";
import PricingPage from "@/pages/PricingPage";
import ProfilePage from "@/pages/ProfilePage";
import ContributePage from "@/pages/ContributePage";
import AdminPage from "@/pages/AdminPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import "@/App.css";

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }
  return children;
};

// Layout with Navbar
const Layout = ({ children, hideNav = false }) => (
  <div className="min-h-screen bg-slate-50">
    {!hideNav && <Navbar />}
    {children}
  </div>
);

// App Router — detects OAuth callback synchronously during render
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect session_id in URL fragment synchronously before any routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout><LandingPage /></Layout>} />
      <Route path="/auth" element={<Layout hideNav><AuthPage /></Layout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/verify" element={<Layout hideNav><VerifyEmailPage /></Layout>} />
      <Route path="/auth/reset-password" element={<Layout hideNav><ResetPasswordPage /></Layout>} />
      <Route path="/contributor/:contributorId" element={<Layout><ContributorProfilePage /></Layout>} />
      <Route path="/apps" element={<Layout><BrowsePage /></Layout>} />
      <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/function/:functionId" element={
        <ProtectedRoute>
          <Layout><FunctionPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/app/:appId" element={
        <ProtectedRoute>
          <Layout><ApplicationPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contribute" element={
        <ProtectedRoute>
          <Layout><ContributePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout><AdminPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <Layout><PaymentSuccessPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
